import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import LanguageSelector from '../components/LanguageSelector';
import './Landing.css';

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const mouse = { x: -1000, y: -1000 };
    const MOUSE_RADIUS = 150;

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    const particles = [];

    class Particle {
      constructor() {
        this.init(width, height);
      }

      init(w, h) {
        // Distribute particles in a brain-like shape (ellipse in center)
        const angle = Math.random() * Math.PI * 2;
        const maxRadius = Math.min(w, h) * 0.4;
        const radius = Math.random() * maxRadius;

        const rx = radius * 1.3;
        const ry = radius * 0.8;

        this.x = w / 2 + Math.cos(angle) * rx;
        this.y = h / 2 + Math.sin(angle) * ry;

        this.baseVx = (Math.random() - 0.5) * 0.6;
        this.baseVy = (Math.random() - 0.5) * 0.6;
        this.vx = this.baseVx;
        this.vy = this.baseVy;

        this.baseX = this.x;
        this.baseY = this.y;

        this.baseSize = Math.random() * 1.5 + 0.5;
        this.size = this.baseSize;

        this.isFlash = Math.random() > 0.95;
        this.currentStyle = this.isFlash ? '#8e24aa' : '#4db6ac';
      }

      update() {
        // Mouse interaction
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distanceToMouse = Math.sqrt(dx * dx + dy * dy);

        if (distanceToMouse < MOUSE_RADIUS) {
          // Excitation effect
          const force = (MOUSE_RADIUS - distanceToMouse) / MOUSE_RADIUS;

          // Slight push away from mouse
          this.vx = this.baseVx - (dx / distanceToMouse) * force * 2;
          this.vy = this.baseVy - (dy / distanceToMouse) * force * 2;

          // Glow / size increase
          this.size = this.baseSize + (force * 2);

          // Color shift towards purple/bright cyan
          this.currentStyle = this.isFlash ? '#b71c1c' : '#8e24aa';
        } else {
          // Return to base state softly
          this.vx += (this.baseVx - this.vx) * 0.1;
          this.vy += (this.baseVy - this.vy) * 0.1;
          this.size += (this.baseSize - this.size) * 0.1;
          this.currentStyle = this.isFlash ? '#8e24aa' : '#4db6ac';
        }

        this.x += this.vx;
        this.y += this.vy;

        // Bounding box logic
        const distFromBaseX = this.x - this.baseX;
        const distFromBaseY = this.y - this.baseY;
        const distFromBase = Math.sqrt(distFromBaseX * distFromBaseX + distFromBaseY * distFromBaseY);

        if (distFromBase > 50) {
          this.baseVx *= -1;
          this.baseVy *= -1;
          // Apply immediate correction to prevent escaping the gravity well
          this.vx += (this.baseVx - this.vx) * 0.5;
          this.vy += (this.baseVy - this.vy) * 0.5;
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.currentStyle;
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles.length = 0;
      const numParticles = Math.min(Math.floor((width * height) / 6000), 250);
      for (let i = 0; i < numParticles; i++) {
        particles.push(new Particle());
      }
    };

    initParticles();

    function animate() {
      ctx.fillStyle = 'rgba(3, 3, 5, 0.2)';
      ctx.fillRect(0, 0, width, height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 80) {
            ctx.beginPath();

            // If near mouse, lines glow stronger
            const distToMouseI = Math.sqrt(Math.pow(mouse.x - particles[i].x, 2) + Math.pow(mouse.y - particles[i].y, 2));
            const distToMouseJ = Math.sqrt(Math.pow(mouse.x - particles[j].x, 2) + Math.pow(mouse.y - particles[j].y, 2));

            let alpha = 1 - dist / 80;
            let strokeColor = `rgba(77, 182, 172, ${alpha})`;

            if (distToMouseI < MOUSE_RADIUS || distToMouseJ < MOUSE_RADIUS) {
               strokeColor = `rgba(142, 36, 170, ${alpha + 0.2})`; // Purple hue for excited connections
            }

            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        p.update();
        p.draw();
      }

      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="landing-container">
      <canvas ref={canvasRef} className="landing-canvas"></canvas>

      <div className="landing-header">
        <div className="landing-brand">QUIDDIT</div>
        <LanguageSelector compact={true} />
      </div>

      <div className="landing-content">
        <h1 className="landing-title glitch-effect" data-text={t('landing.title')}>
          {t('landing.title')}
        </h1>
        <p className="landing-subtitle fade-in-up">{t('landing.subtitle')}</p>

        <div className="landing-actions">
          <button
            className="landing-btn cyberpunk-btn explore-btn"
            onClick={() => navigate('/login')}
          >
            <span className="btn-content">{t('landing.exploreBtn')}</span>
          </button>
          <button
            className="landing-btn cyberpunk-btn register-btn"
            onClick={() => navigate('/register')}
          >
            <span className="btn-content">{t('landing.registerBtn')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
